import type { BellScheduleConfig } from '@/types'
import {
  createDefaultPeriod,
  generatePeriodId,
  parseTimeToMinutes,
  type BellSchedulePeriods,
  type Period
} from '@/types/bell-schedule'

const TIME_CMP_EPS = 1e-4

function gapMinutesBetween(endTime: string, startTime: string): number {
  return Math.round(parseTimeToMinutes(startTime) - parseTimeToMinutes(endTime))
}

function bridgePeriodFromGap(
  startTime: string,
  durationMinutes: number,
  previous: Period | null | undefined,
  defaultName: string,
  type: Period['type'] = 'break'
): Period {
  const id = previous?.id ?? generatePeriodId()
  const name = previous?.name ?? defaultName
  return createDefaultPeriod(id, name, type, startTime, durationMinutes)
}

/**
 * 末段早自习结束至第一节上午正课（classStartTime）之间的空档。
 * 不依赖 `morning` 数组是否已生成，仅用早自习末段与 `classStartTime` 计算间隙。
 */
export function syncMorningStudyToClassBreak(
  next: BellSchedulePeriods,
  previous: BellSchedulePeriods,
  classStartTime: string,
  morningPeriodCount: number
): void {
  if (next.morningStudy.length === 0 || morningPeriodCount === 0) {
    next.morningStudyToClassBreak = null
    return
  }

  const lastStudy = next.morningStudy[next.morningStudy.length - 1]
  const gapMin = gapMinutesBetween(lastStudy.endTime, classStartTime)
  if (gapMin > 0) {
    next.morningStudyToClassBreak = bridgePeriodFromGap(
      lastStudy.endTime,
      gapMin,
      previous.morningStudyToClassBreak,
      '课间休息'
    )
  } else {
    next.morningStudyToClassBreak = null
  }
}

/**
 * 末段下午正课结束至第一节晚自习开始之间的空档。
 */
export function syncAfternoonToEveningBreak(
  next: BellSchedulePeriods,
  previous: BellSchedulePeriods,
  config: BellScheduleConfig,
  eveningStartTime: string
): void {
  if (config.afternoonPeriods === 0 || config.eveningStudy === 0 || next.afternoon.length === 0) {
    next.afternoonToEveningBreak = null
    return
  }

  const lastAfternoon = next.afternoon[next.afternoon.length - 1]
  const gapMin = gapMinutesBetween(lastAfternoon.endTime, eveningStartTime)
  if (gapMin <= 0) {
    next.afternoonToEveningBreak = null
    return
  }

  const big = next.afternoonBreak
  if (big != null) {
    const startsAtLastCourse =
      Math.abs(parseTimeToMinutes(big.startTime) - parseTimeToMinutes(lastAfternoon.endTime)) < TIME_CMP_EPS
    const endsAtEvening =
      Math.abs(parseTimeToMinutes(big.endTime) - parseTimeToMinutes(eveningStartTime)) < TIME_CMP_EPS
    if (startsAtLastCourse && endsAtEvening) {
      next.afternoonToEveningBreak = null
      return
    }
  }

  const defaultName = gapMin >= 60 ? '休息' : '课间休息'
  const prev = previous.afternoonToEveningBreak
  const id = prev?.id ?? generatePeriodId()
  const name = prev?.name ?? defaultName
  next.afternoonToEveningBreak = createDefaultPeriod(
    id,
    name,
    'break',
    lastAfternoon.endTime,
    gapMin
  )
}
